export type RequestPhase = "upload" | "processing" | "download";

const TRANSFER_IDLE_MS = 90 * 1000;
const MAX_UPLOAD_MS = 20 * 60 * 1000;

export function setAudioRequestHeaders(request: XMLHttpRequest, file: File): void {
  // HTTP headers accept byte strings; filenames can contain any Unicode text.
  request.setRequestHeader("X-Filename", encodeURIComponent(file.name));
  request.setRequestHeader("X-File-Size", String(file.size));
  request.setRequestHeader("Content-Type", file.type || "application/octet-stream");
}

export function monitorAudioRequest(
  request: XMLHttpRequest,
  processingTimeoutMs: number,
  onTimeout: (phase: RequestPhase) => void,
) {
  let phase: RequestPhase = "upload";
  let startedAt = 0;
  let uploadPercent = 0;
  let downloadBytes = 0;
  let downloadPercent: number | null = null;
  let finished = false;
  let phaseTimer: ReturnType<typeof setTimeout> | undefined;
  let uploadTimer: ReturnType<typeof setTimeout> | undefined;

  const finish = () => {
    finished = true;
    clearTimeout(phaseTimer);
    clearTimeout(uploadTimer);
    request.upload.removeEventListener("progress", uploadProgress);
    request.upload.removeEventListener("load", uploaded);
    request.removeEventListener("readystatechange", headersReceived);
    request.removeEventListener("progress", downloadProgress);
    request.removeEventListener("loadend", finish);
  };
  const expire = () => {
    if (finished) return;
    finish();
    request.abort();
    onTimeout(phase);
  };
  const arm = (milliseconds: number) => {
    clearTimeout(phaseTimer);
    phaseTimer = setTimeout(expire, milliseconds);
  };
  const uploadProgress = (event: ProgressEvent) => {
    if (finished || phase !== "upload") return;
    if (event.lengthComputable) uploadPercent = Math.round(event.loaded / event.total * 100);
    arm(TRANSFER_IDLE_MS);
  };
  const uploaded = () => {
    if (finished || phase !== "upload") return;
    phase = "processing";
    uploadPercent = 100;
    clearTimeout(uploadTimer);
    arm(processingTimeoutMs);
  };
  const headersReceived = () => {
    if (finished || request.readyState !== XMLHttpRequest.HEADERS_RECEIVED) return;
    phase = "download";
    clearTimeout(uploadTimer);
    arm(TRANSFER_IDLE_MS);
  };
  const downloadProgress = (event: ProgressEvent) => {
    if (finished || phase !== "download" || event.loaded <= downloadBytes) return;
    downloadBytes = event.loaded;
    downloadPercent = event.lengthComputable && event.total > 0 ? Math.round(event.loaded / event.total * 100) : null;
    arm(TRANSFER_IDLE_MS);
  };

  // Attach upload listeners before open() for browsers that require this order.
  request.upload.addEventListener("progress", uploadProgress);
  request.upload.addEventListener("load", uploaded);
  request.addEventListener("readystatechange", headersReceived);
  request.addEventListener("progress", downloadProgress);
  request.addEventListener("loadend", finish);

  return {
    start() {
      startedAt = performance.now();
      arm(TRANSFER_IDLE_MS);
      uploadTimer = setTimeout(expire, MAX_UPLOAD_MS);
    },
    finish,
    diagnostics() {
      return {
        request_phase: phase,
        request_elapsed_seconds: Math.round((performance.now() - startedAt) / 1000),
        upload_progress_percent: uploadPercent,
        download_progress_percent: downloadPercent,
        download_size_bucket: downloadBytes === 0 ? "none" : downloadBytes < 1024 * 1024 ? "under_1_mb"
          : downloadBytes < 10 * 1024 * 1024 ? "1_to_10_mb" : downloadBytes < 50 * 1024 * 1024 ? "10_to_50_mb" : "over_50_mb",
      };
    },
  };
}
